import { supabase } from "@/integrations/supabase/client";
import { moderateImage } from "@/api/moderation";
import type { LngLat } from "@/utils/polyline";

/**
 * Photo-pin API. Native-ready: the file-acquisition step is abstracted into
 * `capturePhotoFile`. Today it triggers a hidden <input> with `capture` so
 * mobile browsers open the system camera. A future Capacitor build can
 * replace just that function with `@capacitor/camera`'s `Camera.getPhoto()`
 * — the rest of the pipeline (resize → upload → insert row) is unchanged.
 */

export interface PhotoPin {
  id: string;
  user_id: string;
  lat: number;
  lng: number;
  caption: string | null;
  photo_path: string;
  is_public: boolean;
  created_at: string;
  /** Resolved signed/public URL, populated by `loadPhotoPins`. */
  photoUrl?: string;
}

const BUCKET = "route-photos";

/** Open the system camera (mobile) or file picker (desktop). */
export function capturePhotoFile(source: "camera" | "library"): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    if (source === "camera") input.setAttribute("capture", "environment");
    // Keep the input in the document — some iOS versions drop the `change`
    // event for detached inputs.
    input.style.display = "none";
    document.body.appendChild(input);

    let settled = false;
    const finish = (f: File | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(f);
    };

    input.onchange = () => finish(input.files?.[0] ?? null);

    // Cancel detection. Real-device bug (Morgan: taking a photo "took a few
    // tries"): after the camera closes, iOS fires window `focus` BEFORE the
    // captured file is attached — HEIC processing regularly exceeds the old
    // 800 ms grace, so successful captures were mis-read as cancels and
    // silently dropped. Give the change event a generous 3.5 s after focus
    // returns; a genuine cancel just resolves null a beat later.
    const onFocus = () => {
      setTimeout(() => finish(input.files?.[0] ?? null), 3_500);
    };
    window.addEventListener("focus", onFocus, { once: true });

    input.click();
  });
}

/**
 * Resize a photo to a max dimension and re-encode as JPEG to keep uploads
 * snappy (typical phone photos are 4–8 MB → ~300 KB after this).
 */
async function resizeImage(file: File, maxDim = 1600, quality = 0.82): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D not available");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Failed to encode image"))),
      "image/jpeg",
      quality,
    );
  });
}

/** Upload the photo + insert a row in photo_pins. Returns the new pin. */
export async function createPhotoPin(args: {
  file: File;
  location: LngLat;
  caption?: string;
  isPublic: boolean;
}): Promise<PhotoPin> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) throw new Error("Sign in required to add photos");
  const userId = userData.user.id;

  const blob = await resizeImage(args.file);

  // Moderate the (already-resized) image BEFORE uploading. Throws on block.
  await moderateImage(blob, "image/jpeg");

  const id = crypto.randomUUID();
  const path = `${userId}/${id}.jpg`;

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: "image/jpeg", upsert: false });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    .from("photo_pins")
    .insert({
      user_id: userId,
      lat: args.location[1],
      lng: args.location[0],
      caption: args.caption?.trim() || null,
      photo_path: path,
      is_public: args.isPublic,
    })
    .select()
    .single();
  if (error) {
    // Roll back the orphan upload if the row insert failed.
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw error;
  }
  const url = await resolvePhotoUrl(data.photo_path, data.is_public);
  return { ...(data as PhotoPin), photoUrl: url };
}

/** Resolve a photo path to a viewable URL. Public pins use the public URL,
 *  private pins use a short-lived signed URL. */
async function resolvePhotoUrl(path: string, isPublic: boolean): Promise<string> {
  if (isPublic) {
    return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
  }
  const { data } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60);
  return data?.signedUrl ?? "";
}

/** Fetch every pin the current user is allowed to see, with photo URLs.
 *
 *  URL resolution is split:
 *    - Public pins use `getPublicUrl` (sync, no network).
 *    - Private pins are signed in batches via `createSignedUrls` (one
 *      network request per batch instead of one per pin) so loading 100s
 *      of pins doesn't fan out into 100s of parallel requests at startup. */
export async function loadPhotoPins(): Promise<PhotoPin[]> {
  const { data, error } = await supabase
    .from("photo_pins")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  const rows = (data ?? []) as PhotoPin[];

  // Public pins: cheap.
  for (const r of rows) {
    if (r.is_public) {
      r.photoUrl = supabase.storage.from(BUCKET).getPublicUrl(r.photo_path).data.publicUrl;
    }
  }

  // Private pins: batch-sign so we don't hammer storage with hundreds of
  // parallel signed-URL requests on cold start.
  const privatePaths = rows.filter((r) => !r.is_public).map((r) => r.photo_path);
  if (privatePaths.length > 0) {
    const byPath = new Map<string, string>();
    const BATCH = 50;
    for (let i = 0; i < privatePaths.length; i += BATCH) {
      const slice = privatePaths.slice(i, i + BATCH);
      const { data: signed } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(slice, 60 * 60);
      for (const s of signed ?? []) {
        if (s.path && s.signedUrl) byPath.set(s.path, s.signedUrl);
      }
    }
    for (const r of rows) {
      if (!r.is_public) r.photoUrl = byPath.get(r.photo_path) ?? "";
    }
  }
  return rows;
}

export async function setPinVisibility(id: string, isPublic: boolean): Promise<void> {
  const { error } = await supabase
    .from("photo_pins")
    .update({ is_public: isPublic })
    .eq("id", id);
  if (error) throw error;
}

export async function deletePhotoPin(pin: Pick<PhotoPin, "id" | "photo_path">): Promise<void> {
  // Storage policies allow the owner to delete their own files; row RLS
  // restricts the row delete to the owner too.
  await supabase.storage.from(BUCKET).remove([pin.photo_path]).catch(() => {});
  const { error } = await supabase.from("photo_pins").delete().eq("id", pin.id);
  if (error) throw error;
}
